import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { isQRCodeError, SessionStorageKeys } from '@app/shared/constants/auth.constants';
import { SpeakseeAuthService, UserModel, UserTokenModel } from '@speaksee/speaksee-angular-core';
import { AccountService } from '@app/services/account.service';
import { first } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { GuestSessionService } from '@app/services/guest-session.service';
import { inject } from '@angular/core';
import { SettingsService } from '@app/services/settings.service';
import { SharedSessionAuthService } from '@app/services/shared-session-auth.service';

// Auth expiration in ms (1 hour)
const AUTH_EXPIRATION = 60 * 60 * 1000;


/**
 * Checks if the given user access|refresh token have expired.
 */
function isTokenExpired(token: UserTokenModel): boolean {
  const now = new Date().getTime();
  return token.accessExpireAt < now && token.refreshExpireAt < now;
}

/**
 * Checks if the given route has a user token in its query parameters.
 * Handles both regular user tokens and av-view specific tokens.
 */
function checkIfRouteHasUserToken(route: ActivatedRouteSnapshot, isAvView: boolean = false): UserModel | null {
  const token = route.queryParamMap.get('token');
  if (!token) {
    // For av-view, also check session storage
    if (isAvView) {
      const deviceId = route.params['id'];
      const sessionToken = sessionStorage.getItem(`av-view-token-${deviceId}`);
      if (sessionToken) {
        try {
          const decodedToken = JSON.parse(atob(sessionToken));
          return decodedToken;
        } catch (e) {
          console.error('Failed to parse session storage token:', e);
        }
      }
    }
    return null;
  }

  // Store token in session for av-view routes
  if (isAvView) {
    const deviceId = route.params['id'];
    sessionStorage.setItem(`av-view-token-${deviceId}`, token);
  }

  try {
    const decodedToken = JSON.parse(atob(token));
    if (Object.keys(decodedToken).length) {
      return decodedToken;
    }
  } catch (e) {
    console.error('Failed to parse base64 token:', e);
  }
  return null;
}

/**
 * Redirects the user to the login page with a return URL query parameter.
 */
function redirectToLogin(router: Router, redirectURL?: string): Promise<boolean> {
  const params: any = {};
  if (redirectURL) {
    params['returnUrl'] = redirectURL;
  }
  return router.navigate(['/login'], params);
}

/**
 * Functional guard for authentication.
 * Handles QR code login, av-view token authentication, and standard user authentication.
 */
export const authGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean> => {

  const router = inject(Router);
  const accountService = inject(AccountService);
  const authService = inject(SpeakseeAuthService);
  const qrUserService = inject(GuestSessionService);
  const settingsService = inject(SettingsService);
  const sharedAuth = inject(SharedSessionAuthService);

  if (sharedAuth.isActive()) {
    if (await sharedAuth.ensureValidToken()) return true;
    await router.navigate(['/join']);
    return false;
  }

  // auto logout if auth mode is qr and auth timestamp is older than 1 hour
  if (
    sessionStorage.getItem(SessionStorageKeys.AUTH_MODE) === 'qr' &&
    +sessionStorage.getItem(SessionStorageKeys.AUTH_TIMESTAMP) + AUTH_EXPIRATION <= new Date().getTime()
  ) {
    await accountService.logout();
  }

  // Try to login with QR code if it exists
  const signInCode = route.queryParamMap.get('signInCode');
  if (signInCode) {
    try {
      await accountService.logout();
      await accountService.loginByQRCode(signInCode).toPromise();

      // Extract and store device UUID - try both query params and route params
      const deviceId = route.queryParamMap.get('deviceID') || route.params['deviceID'];
      if (deviceId) {
        qrUserService.storeDeviceUuid(deviceId);
      }

      // Store firestoredb from route params (speech/:id)
      const firestoredb = route.params['id'];
      if (firestoredb) {
        qrUserService.storeFirestoreDb(firestoredb);
      }

      // Determine device type from license data (soundshape vs microphonekit)
      await qrUserService.determineAndStoreDeviceType();

      // Only initialize QR websocket for soundshape users - microphonekit uses frontend translation
      if (qrUserService.isSoundshapeQR()) {
        await firstValueFrom(qrUserService.initializeWebsocket());
      }

      await router.navigateByUrl(route.url.join('/'), {
        replaceUrl: true,
      });

      const darkMode = route.queryParamMap.get('darkMode');
      if (darkMode === 'true') {
        settingsService.setQrDarkMode(true);
      } else if (darkMode === 'false') {
        settingsService.setQrDarkMode(false);
      } else {
        // Clear any previous QR dark mode preference
        settingsService.clearQrDarkMode();
      }

    } catch (e: any) {
      console.error(e);

      // Store for potential retry
      sessionStorage.setItem(SessionStorageKeys.QR_SIGN_IN_CODE, signInCode);
      sessionStorage.setItem(SessionStorageKeys.QR_ORIGINAL_URL, route.url.join('/'));

      // Check if error has type information for better routing
      if (isQRCodeError(e)) {
        const queryParams: any = { type: e.type };

        if (e.startDate) {
          queryParams.startDate = e.startDate;
        }
        if (e.endDate) {
          queryParams.endDate = e.endDate;
        }

        await router.navigate(['/access-restricted'], { queryParams });
        return false;
      }

      // Default to 403 for other errors
      await router.navigateByUrl('/403');
      return false;
    }
  }

  const isAvViewRoute = route.url.some(segment => segment.path === 'av-view');
  const userToken = checkIfRouteHasUserToken(route, isAvViewRoute);

  if (userToken) {
    authService.updateUser(userToken);

    // For av-view routes, store additional data for the resolver
    if (isAvViewRoute) {
      const tokenParam = route.queryParamMap.get('token');
      const jwtToken = userToken?.token?.accessToken;

      // Store extracted data in route data for resolver to use
      route.data = {
        ...route.data,
        avViewAuth: {
          jwtToken,
          tokenData: userToken,
          shouldCleanUrl: !!tokenParam // Clean URL if token was in query params
        }
      };

      return true;
    }

    // For non-av-view routes, clean the URL immediately
    const queryParams = { ...route.queryParams };
    delete queryParams['token'];

    await router.navigate([route.url.join('/')], {
      queryParams,
      replaceUrl: true
    });
  }

  // Handling for av-view routes without token
  if (isAvViewRoute) {
    const deviceId = route.params['id'];
    const sessionToken = sessionStorage.getItem(`av-view-token-${deviceId}`);

    if (sessionToken) {
      try {
        const decodedToken = JSON.parse(atob(sessionToken));
        authService.updateUser(decodedToken);

        route.data = {
          ...route.data,
          avViewAuth: {
            jwtToken: decodedToken?.token?.accessToken,
            tokenData: decodedToken,
            shouldCleanUrl: false
          }
        };

        return true;
      } catch (error) {
        console.error('Failed to parse session token:', error);
        return redirectToLogin(router, state.url);
      }
    } else {
      console.error('No token available for av-view authentication');
      return redirectToLogin(router, state.url);
    }
  }

  const user = accountService.userValue;
  if (!user?.token) {
    return redirectToLogin(router, state.url);
  }

  // logout if (access|refresh)token has expired
  if (isTokenExpired(user.token)) {
    accountService.logout();
    return false;
  }

  try {
    accountService.refresh().pipe(first()).toPromise();
  } catch {
    return redirectToLogin(router, state.url);
  }

  return true;
};
